import React, { useState, useEffect, useRef } from "react";
import { Button, message } from "antd";
import {
  CreditCardOutlined,
  QuestionCircleOutlined,
  CheckOutlined,
  CloseOutlined
} from "@ant-design/icons";
import { useCallback } from "react";
import facturaApi from "../../modules/admin/facturas/store/api";
import clientBillingApi from "../../modules/client/billing/store/api";

const PayInvoiceButton = ({
  facturaId,
  onSuccess,
  onError,
  disabled = false,
  size = "small",
  isClientSide = false,
  facturaStatus,
  className = ""
}) => {
  const [buttonState, setButtonState] = useState("initial"); // initial, disabled, confirm, processing, success, error
  const timeoutRef = useRef(null);

  const DEBOUNCE_TIME = 700; // 700ms debounce
  const OUTCOME_DISPLAY_TIME = 700; // 700ms to show success/error

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const resetToInitial = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setButtonState("initial");
  }, []);

  const executePayment = useCallback(async () => {
    setButtonState("processing");

    try {
      const api = isClientSide ? clientBillingApi : facturaApi;
      const result = await api.payInvoice(facturaId);

      if (result.success) {
        message.success("Payment successful!");
        setButtonState("success");

        // Show success state for 700ms, then trigger success callback
        timeoutRef.current = setTimeout(() => {
          if (onSuccess) {
            onSuccess(result);
          }
          resetToInitial();
        }, OUTCOME_DISPLAY_TIME);
      } else {
        throw new Error("Payment was not successful");
      }
    } catch (error) {
      console.error("Payment error:", error);
      message.error(`Payment failed: ${error.message}`);
      setButtonState("error");

      // Show error state for 700ms, then reset
      timeoutRef.current = setTimeout(() => {
        if (onError) {
          onError(error);
        }
        resetToInitial();
      }, OUTCOME_DISPLAY_TIME);
    }
  }, [facturaId, onSuccess, onError, isClientSide, resetToInitial]);

  const handleButtonClick = useCallback(
    (e) => {
      e.stopPropagation();

      if (facturaStatus === "paid") {
        message.info("Invoice is already paid");
        return;
      }

      if (buttonState === "initial") {
        // First click: start debounce phase
        setButtonState("disabled");

        timeoutRef.current = setTimeout(() => {
          setButtonState("confirm");
        }, DEBOUNCE_TIME);
      } else if (buttonState === "confirm") {
        // Second click: execute payment
        executePayment();
      }
      // Ignore clicks in other states
    },
    [buttonState, facturaStatus, executePayment]
  );

  // Don't show button for paid invoices
  if (facturaStatus === "paid") {
    return null;
  }

  // Get button properties based on state
  const getButtonProps = () => {
    switch (buttonState) {
      case "initial":
        return {
          type: "primary",
          icon: <CreditCardOutlined />,
          disabled: disabled,
          style: {
            backgroundColor: "#52c41a",
            borderColor: "#52c41a",
            color: "white"
          },
          text: "Pay Invoice"
        };

      case "disabled":
        return {
          type: "default",
          icon: <CreditCardOutlined />,
          disabled: true,
          style: {
            backgroundColor: "#f5f5f5",
            borderColor: "#d9d9d9",
            color: "#bfbfbf"
          },
          text: "Pay Invoice"
        };

      case "confirm":
        return {
          type: "primary",
          icon: <QuestionCircleOutlined />,
          disabled: disabled,
          style: {
            backgroundColor: "#faad14",
            borderColor: "#faad14",
            color: "white"
          },
          text: "Confirm"
        };

      case "processing":
        return {
          type: "primary",
          icon: <CreditCardOutlined />,
          disabled: true,
          loading: true,
          style: {
            backgroundColor: "#1890ff",
            borderColor: "#1890ff",
            color: "white"
          },
          text: "Processing..."
        };

      case "success":
        return {
          type: "default",
          icon: <CheckOutlined />,
          disabled: true,
          style: {
            backgroundColor: "white",
            borderColor: "#52c41a",
            color: "#52c41a"
          },
          text: "Success"
        };

      case "error":
        return {
          type: "default",
          icon: <CloseOutlined />,
          disabled: true,
          style: {
            backgroundColor: "white",
            borderColor: "#ff4d4f",
            color: "#ff4d4f"
          },
          text: "Failed"
        };

      default:
        return {
          type: "primary",
          icon: <CreditCardOutlined />,
          disabled: disabled,
          style: {
            backgroundColor: "#52c41a",
            borderColor: "#52c41a",
            color: "white"
          },
          text: "Pay Invoice"
        };
    }
  };

  const buttonProps = getButtonProps();

  return (
    <Button
      type={buttonProps.type}
      size={size}
      icon={buttonProps.icon}
      loading={buttonProps.loading}
      disabled={buttonProps.disabled}
      onClick={handleButtonClick}
      className={`pay-invoice-button ${className}`}
      style={buttonProps.style}
    >
      {buttonProps.text}
    </Button>
  );
};

export default PayInvoiceButton;
